import { v4 as uuidv4 } from 'uuid'
import { IS_EDITOR_PREVIEW } from '../storefront/context/QueryParamContextProvider'
import { runWhenReady } from '../storefront/components/pixels/pixelRuntime'
import { FBEvent, MetaPixelClientSidePayload } from './types'
import { BlackCrowLogger } from '../storefront/analytics/blackcrow'

/**
 * Build the object passed to `fbq('track', ...)` and to BlackCrow.
 */
function buildClientSidePixelPayload(
    event: FBEvent,
): Partial<MetaPixelClientSidePayload> {
    return {
        ...(typeof event.num_items === 'number' &&
            Number.isFinite(event.num_items) && { num_items: event.num_items }),
        ...(event.content_type && { content_type: event.content_type }),
        ...(event.content_name && { content_name: event.content_name }),
        ...(event.content_ids?.length && { content_ids: event.content_ids }),
        ...(event.content_category && {
            content_category: event.content_category,
        }),
        ...(typeof event.value === 'number' &&
            Number.isFinite(event.value) && { value: event.value }),
        ...(event.currency && { currency: event.currency }),
        ...(event.contents?.length && { contents: event.contents }),
        ...(event.order_id != null &&
            event.order_id !== '' && { order_id: event.order_id }),
    } satisfies Partial<MetaPixelClientSidePayload>
}

/**
 * Trigger Facebook PageView Event (Standard Pixel).
 *
 * @constructor
 */
const fbPageView = (): void => {
    runWhenReady('meta', () => {
        window.fbq?.('track', 'PageView')
    })
}

/**
 * Trigger client side Facebook Event.
 *
 * @param event
 * @constructor
 */
const fbEvent = (event: FBEvent): void => {
    if (IS_EDITOR_PREVIEW()) return

    const eventId = event.eventId ? event.eventId : uuidv4()
    const eventName = event.eventName

    const clientSidePayload = buildClientSidePixelPayload(event)

    runWhenReady('meta', () => {
        window.fbq?.('track', eventName, clientSidePayload, {
            eventID: eventId,
        })
    })

    // Trigger blackcrow server side event
    BlackCrowLogger.triggerStorefrontsAnalyticsEvent(
        eventName,
        eventId,
        clientSidePayload,
        'storefronts',
    )
}

export { fbEvent, fbPageView }
